import React, { useEffect, useState } from "react";
import "./AnniversaryCelebration.css";

const highlights = [
  {
    icon: "♡",
    title: "A Private, Intimate Space",
    description: "Enjoy your special day together, away from the usual crowds.",
  },
  {
    icon: "✧",
    title: "Ambient Theatre Decor",
    description: "Arrive at a beautifully pre-decorated theatre, ready to celebrate.",
  },
  {
    icon: "🎂",
    title: "Cake & Food",
    description: "Share a sweet moment and enjoy food for your occasion.",
  },
  {
    icon: "💐",
    title: "Bouquets & Gifts",
    description: "Make your partner feel special with a thoughtful surprise.",
  },
  {
    icon: "♫",
    title: "Personalized Moments",
    description: "Add a customized song, a surprise reveal or a private movie.",
  },
];

const steps = [
  {
    number: "01",
    title: "Tell Us Your Plans",
    description:
      "Share your occasion, preferred date, time and number of guests.",
  },
  {
    number: "02",
    title: "Choose Your Add-Ons",
    description:
      "Select the extras you would like, such as cake, food, gifts or special experiences.",
  },
  {
    number: "03",
    title: "Arrive & Celebrate",
    description:
      "Once your slot is confirmed, walk into your pre-decorated private theatre and enjoy the moment.",
  },
];

const addOns = [
  {
    icon: "🎂",
    title: "Anniversary Cake",
    description: "Share a sweet moment made for the two of you.",
    detail:
      "Add an anniversary cake to mark your special milestone. Cake options, availability and prices can be confirmed with the HexaPlay team.",
  },
  {
    icon: "💐",
    title: "Bouquets & Gifts",
    description: "Make your partner's surprise more meaningful.",
    detail:
      "Explore bouquets and personalized gifts to make your anniversary surprise more memorable. Available choices may vary.",
  },
  {
    icon: "♫",
    title: "Customized Song",
    description: "Make your favorite song part of the celebration.",
    detail:
      "Ask about arranging a customized song or a special musical moment for your partner. Confirm the available options with HexaPlay.",
  },
  {
    icon: "🎨",
    title: "Canvas Painting",
    description: "Keep a creative memory of your journey together.",
    detail:
      "Explore canvas painting as a keepsake from your anniversary experience. Availability and package details should be confirmed before booking.",
  },
  {
    icon: "✨",
    title: "Surprise Entry",
    description: "Create an unforgettable reveal for your partner.",
    detail:
      "Ask the team about available surprise options, including fog entry and special reveals, to create a memorable arrival.",
  },
  {
    icon: "▶",
    title: "Private Movie",
    description: "Enjoy a movie that means something to both of you.",
    detail:
      "Make time for a movie in your private theatre. Confirm available entertainment options and any applicable restrictions with HexaPlay.",
  },
];

const faqs = [
  {
    question: "Is the anniversary celebration private?",
    answer:
      "Yes. The theatre is booked for your group only, so your celebration stays private.",
  },
  {
    question: "Can I surprise my partner?",
    answer:
      "Yes. Our team can help you plan the surprise with available options such as fog entry, gifts and a customized song. Share your plan when discussing your booking.",
  },
  {
    question: "Can I customize the theatre decoration?",
    answer:
      "The theatre has a fixed, premium, pre-installed decor setup. The decoration itself cannot be customized, but you can personalize your experience through available add-ons.",
  },
  {
    question: "What add-ons can I include?",
    answer:
      "Options may include cake, bouquets, gifts, a customized song, canvas painting, food and special entrance effects. Please confirm availability and prices with HexaPlay.",
  },
  {
    question: "Can we watch a movie together?",
    answer:
      "A private movie experience can be part of your celebration. Discuss your preferred entertainment and the available options with the HexaPlay team.",
  },
];

const popupContent = {
  plan: {
    icon: "♡",
    title: "Plan Your Anniversary",
    description:
      "Create a meaningful celebration for your relationship in a private, pre-decorated HexaPlay theatre.",
    points: [
      "Share your anniversary date and preferred time.",
      "Tell us the number of guests attending.",
      "Discuss your preferred cake, food, gifts and entertainment.",
      "Confirm the available slot, package and pricing with the team.",
    ],
    note:
      "This popup provides planning information only. Your date and theatre slot are not booked or reserved here.",
  },
  addons: {
    icon: "✧",
    title: "Explore Anniversary Add-Ons",
    description:
      "Choose thoughtful extras to make your celebration feel personal and memorable.",
    points: [
      "Anniversary cakes and celebration treats.",
      "Bouquets and personalized gifts.",
      "Customized songs and surprise moments.",
      "Canvas painting keepsakes.",
      "Fog entry and private movie experiences.",
    ],
    note:
      "Options, availability and prices may vary. Contact the HexaPlay team to confirm what is currently offered.",
  },
  album: {
    icon: "📸",
    title: "Anniversary Celebration Gallery",
    description:
      "Discover inspiration for intimate anniversary moments, romantic surprises and couple celebrations.",
    points: [
      "Anniversary celebration inspiration.",
      "Couple and cake-cutting moments.",
      "Surprise entries and personalized experiences.",
    ],
    note:
      "Use real HexaPlay anniversary and couple celebration photos in the album section. The gallery on this page is currently a set of photo placeholders.",
  },
};

const AnniversaryCelebration = () => {
  const [activePopup, setActivePopup] = useState(null);

  const openPopup = (content) => {
    setActivePopup(content);
  };

  const closePopup = () => {
    setActivePopup(null);
  };

  useEffect(() => {
    if (!activePopup) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActivePopup(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activePopup]);

  return (
    <main className="anniversary-page">
      {/* Hero */}
      <section className="anniversary-hero">
        <div className="anniversary-glow anniversary-glow-one" />
        <div className="anniversary-glow anniversary-glow-two" />

        <div className="anniversary-container anniversary-hero-content">
          <span className="anniversary-eyebrow">
            EVERY LOVE STORY DESERVES A MOMENT
          </span>

          <h1>
            Anniversary Celebration
            <span> in a Private Theatre</span>
          </h1>

          <p className="anniversary-hero-description">
            Celebrate your journey together with an anniversary experience
            created around your special day. At HexaPlay, couples can enjoy a
            private setting with ambient, pre-set celebration decor, away from
            the crowd and the noise of a regular restaurant outing.
          </p>

          <p className="anniversary-hero-description">
            Watch a movie that means something to both of you, share a cake and
            add thoughtful details such as bouquets, gifts or a customized song
            to make the day more meaningful.
          </p>

          <div className="anniversary-hero-actions">
            <button
              type="button"
              className="anniversary-btn anniversary-btn-primary"
              onClick={() => openPopup(popupContent.plan)}
            >
              Plan Your Anniversary <span aria-hidden="true">↗</span>
            </button>

            <button
              type="button"
              className="anniversary-btn anniversary-btn-secondary"
              onClick={() => openPopup(popupContent.addons)}
            >
              Explore Add-Ons
            </button>
          </div>

          <div className="anniversary-hero-tags">
            <span>Private Celebration</span>
            <span>Ambient Theatre Decor</span>
            <span>Personalized Experiences</span>
          </div>

          <div className="anniversary-hero-caption">
            <span className="anniversary-caption-line" />
            <span>Just the two of you. A moment to remember.</span>
            <span className="anniversary-caption-line" />
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="anniversary-section anniversary-highlights-section">
        <div className="anniversary-container">
          <div className="anniversary-section-heading">
            <span className="anniversary-eyebrow">
              MADE FOR YOUR STORY
            </span>
            <h2>Make Your Anniversary Special With</h2>
            <p>
              Thoughtful details, private moments and meaningful experiences
              come together to celebrate your journey.
            </p>
          </div>

          <div className="anniversary-highlights-grid">
            {highlights.map((item, index) => (
              <article className="anniversary-highlight-card" key={item.title}>
                <span className="anniversary-card-number">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="anniversary-highlight-icon" aria-hidden="true">
                  {item.icon}
                </span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="anniversary-section anniversary-process-section">
        <div className="anniversary-container">
          <div className="anniversary-section-heading">
            <span className="anniversary-eyebrow">
              SIMPLE, THOUGHTFUL, MEMORABLE
            </span>
            <h2>How It Works</h2>
            <p>
              Three simple steps to plan your private anniversary celebration.
            </p>
          </div>

          <div className="anniversary-process-grid">
            {steps.map((step) => (
              <article className="anniversary-process-card" key={step.number}>
                <span className="anniversary-process-number">
                  {step.number}
                </span>
                <div className="anniversary-process-line" />
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="anniversary-section anniversary-addons-section">
        <div className="anniversary-container">
          <div className="anniversary-section-heading">
            <span className="anniversary-eyebrow">
              THE DETAILS THAT MAKE IT YOURS
            </span>
            <h2>Popular Anniversary Add-Ons</h2>
            <p>
              From a cake and bouquet to a customized song or canvas painting,
              choose thoughtful touches that reflect your story.
            </p>
          </div>

          <div className="anniversary-addons-grid">
            {addOns.map((item) => (
              <article className="anniversary-addon-card" key={item.title}>
                <div className="anniversary-addon-icon" aria-hidden="true">
                  {item.icon}
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <button
                  type="button"
                  className="anniversary-addon-link"
                  onClick={() =>
                    openPopup({
                      icon: item.icon,
                      title: item.title,
                      description: item.detail,
                      points: [
                        "Ask about current availability.",
                        "Confirm package inclusions and pricing.",
                        "Discuss your preferences before your slot is confirmed.",
                      ],
                      note:
                        "This is informational only. No add-on is selected or purchased through this popup.",
                    })
                  }
                >
                  Learn More <span aria-hidden="true">↗</span>
                </button>
              </article>
            ))}
          </div>

          <div className="anniversary-decoration-note">
            <span className="anniversary-decoration-note-icon" aria-hidden="true">
              ✧
            </span>
            <div>
              <h3>A Note About Theatre Decoration</h3>
              <p>
                Every HexaPlay private theatre has a fixed, premium decor setup
                that is pre-installed. The decoration cannot be customized, so
                you simply walk in and start celebrating. Personalize the
                experience with available add-ons instead.
              </p>
            </div>
          </div>

          <div className="anniversary-centered-action">
            <button
              type="button"
              className="anniversary-btn anniversary-btn-primary"
              onClick={() => openPopup(popupContent.addons)}
            >
              Explore Add-Ons <span aria-hidden="true">↗</span>
            </button>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="anniversary-section anniversary-gallery-section">
        <div className="anniversary-container">
          <div className="anniversary-section-heading">
            <span className="anniversary-eyebrow">
              MOMENTS THAT STAY WITH YOU
            </span>
            <h2>Anniversary Celebration Moments</h2>
            <p>
              Find inspiration for your special day with couple celebrations,
              romantic surprises and meaningful keepsakes.
            </p>
          </div>

          <div className="anniversary-gallery-grid">
            <article className="anniversary-gallery-card anniversary-gallery-featured">
              <span className="anniversary-gallery-symbol" aria-hidden="true">
                ♡
              </span>
              <div>
                <span className="anniversary-gallery-label">
                  YOUR STORY, YOUR MOMENT
                </span>
                <h3>Anniversary Celebrations</h3>
                <p>Add a real HexaPlay anniversary celebration photo.</p>
              </div>
            </article>

            <article className="anniversary-gallery-card">
              <span className="anniversary-gallery-symbol" aria-hidden="true">
                🎂
              </span>
              <div>
                <h3>Cake & Togetherness</h3>
                <p>Add a real HexaPlay couple celebration photo.</p>
              </div>
            </article>

            <article className="anniversary-gallery-card">
              <span className="anniversary-gallery-symbol" aria-hidden="true">
                💐
              </span>
              <div>
                <h3>Surprises & Keepsakes</h3>
                <p>Add a real HexaPlay surprise celebration photo.</p>
              </div>
            </article>
          </div>

          <p className="anniversary-gallery-note">
            Replace the placeholders with real HexaPlay anniversary and couple
            celebration photos before publishing.
          </p>

          <div className="anniversary-centered-action">
            <button
              type="button"
              className="anniversary-btn anniversary-btn-secondary"
              onClick={() => openPopup(popupContent.album)}
            >
              View Album <span aria-hidden="true">↗</span>
            </button>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="anniversary-section anniversary-faq-section">
        <div className="anniversary-container anniversary-faq-layout">
          <div className="anniversary-faq-intro">
            <span className="anniversary-eyebrow">GOOD TO KNOW</span>
            <h2>Frequently Asked Questions</h2>
            <p>
              A few helpful answers to make planning your anniversary
              celebration easier.
            </p>

            <button
              type="button"
              className="anniversary-faq-contact"
              onClick={() => openPopup(popupContent.plan)}
            >
              Plan your special moment <span aria-hidden="true">↗</span>
            </button>
          </div>

          <div className="anniversary-faq-list">
            {faqs.map((faq, index) => (
              <details className="anniversary-faq-item" key={faq.question}>
                <summary>
                  <span className="anniversary-faq-index">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="anniversary-faq-question">
                    {faq.question}
                  </span>
                  <span className="anniversary-faq-toggle" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="anniversary-final-section">
        <div className="anniversary-container anniversary-final-content">
          <span className="anniversary-eyebrow">
            CELEBRATE THE JOURNEY YOU SHARE
          </span>
          <h2>One Love. Countless Memories.</h2>
          <p>
            Make your anniversary meaningful with a private theatre, thoughtful
            surprises and moments created just for the two of you.
          </p>

          <button
            type="button"
            className="anniversary-btn anniversary-btn-primary"
            onClick={() => openPopup(popupContent.plan)}
          >
            Plan Your Anniversary <span aria-hidden="true">↗</span>
          </button>
        </div>
      </section>

      {/* Informational Popup */}
      {activePopup && (
        <div
          className="anniversary-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closePopup();
          }}
        >
          <section
            className="anniversary-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="anniversary-modal-title"
            aria-describedby="anniversary-modal-description"
          >
            <button
              type="button"
              className="anniversary-modal-close"
              onClick={closePopup}
              aria-label="Close popup"
              autoFocus
            >
              ×
            </button>

            <div className="anniversary-modal-icon" aria-hidden="true">
              {activePopup.icon}
            </div>

            <span className="anniversary-eyebrow">
              HEXAPLAY ANNIVERSARY EXPERIENCES
            </span>

            <h2 id="anniversary-modal-title">{activePopup.title}</h2>

            <p
              id="anniversary-modal-description"
              className="anniversary-modal-description"
            >
              {activePopup.description}
            </p>

            <ul className="anniversary-modal-points">
              {activePopup.points.map((point) => (
                <li key={point}>
                  <span aria-hidden="true">✓</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <p className="anniversary-modal-note">{activePopup.note}</p>

            <button
              type="button"
              className="anniversary-btn anniversary-btn-primary anniversary-modal-done"
              onClick={closePopup}
            >
              Got It
            </button>
          </section>
        </div>
      )}
    </main>
  );
};

export default AnniversaryCelebration;
